import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import AppLayout from './components/shared/AppLayout';

// Public
import LoginPage from './pages/LoginPage';
import BookingPage from './pages/public/BookingPage';

// Admin
import DashboardPage from './pages/admin/DashboardPage';
import AppointmentsPage from './pages/admin/AppointmentsPage';
import CalendarPage from './pages/admin/CalendarPage';
import ServicesPage from './pages/admin/ServicesPage';
import WorkersPage from './pages/admin/WorkersPage';
import ClientsPage from './pages/admin/ClientsPage';
import ReportsPage from './pages/admin/ReportsPage';
import FinancePage from './pages/admin/FinancePage';
import BusinessPage from './pages/admin/BusinessPage';
import BranchesPage from './pages/admin/BranchesPage';
import AdvancedReportsPage from './pages/admin/AdvancedReportsPage';
import SupportPage from './pages/admin/SupportPage';

// Barber
import BarberCalendarPage from './pages/barber/BarberCalendarPage';
import BarberAppointmentsPage from './pages/barber/BarberAppointmentsPage';

// Superadmin
import SuperDashboard from './pages/superadmin/SuperDashboard';
import BusinessesPage from './pages/superadmin/BusinessesPage';
import PlansPage from './pages/superadmin/PlansPage';
import UsersPage from './pages/superadmin/UsersPage';
import SuperReportsPage from './pages/superadmin/SuperReportsPage';
import TicketsPage from './pages/superadmin/TicketsPage';

function ProtectedRoute({ children, roles }) {
  const { user, loading } = useAuth();
  if (loading) return <div style={{ display:'flex', alignItems:'center', justifyContent:'center', height:'100vh', color:'var(--color-gold)', fontFamily:'var(--font-display)', fontSize:'1.2rem' }}>Cargando...</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (roles && !roles.includes(user.role)) return <Navigate to="/unauthorized" replace />;
  return children;
}

// Inicio del panel de negocio: muestra el Dashboard solo si el plan tiene ese
// modulo activo; si no, entra directo al calendario.
function AdminHome() {
  const { hasPlan } = useAuth();
  return hasPlan('has_dashboard') ? <DashboardPage /> : <Navigate to="/admin/calendar" replace />;
}

function RoleHome() {
  const { user } = useAuth();
  if (!user) return <Navigate to="/login" replace />;
  if (user.role === 'super_admin') return <Navigate to="/superadmin" replace />;
  if (user.role === 'barbero') return <Navigate to="/barber/calendar" replace />;
  return <Navigate to="/admin" replace />;
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          {/* Public */}
          <Route path="/login" element={<LoginPage />} />
          <Route path="/book/:slug" element={<BookingPage />} />
          <Route path="/" element={<RoleHome />} />
          <Route path="/unauthorized" element={
            <div style={{ display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', height:'100vh', gap:'1rem' }}>
              <h1 style={{ color:'var(--color-gold)' }}>Sin acceso</h1>
              <p style={{ color:'var(--color-text-muted)' }}>No tienes permisos para esta sección.</p>
              <a href="/" className="btn btn-primary">Volver al inicio</a>
            </div>
          } />

          {/* Admin / Admin General routes */}
          <Route path="/admin" element={
            <ProtectedRoute roles={['admin', 'admin_general']}>
              <AppLayout />
            </ProtectedRoute>
          }>
            <Route index element={<AdminHome />} />
            <Route path="appointments" element={<AppointmentsPage />} />
            <Route path="calendar" element={<CalendarPage />} />
            <Route path="services" element={<ServicesPage />} />
            <Route path="workers" element={<WorkersPage />} />
            <Route path="clients" element={<ClientsPage />} />
            <Route path="reports" element={<ReportsPage />} />
            <Route path="finance" element={<FinancePage />} />
            <Route path="business" element={<BusinessPage />} />
            <Route path="hours" element={<BusinessPage defaultTab="horarios" />} />
            <Route path="branches" element={<BranchesPage />} />
            <Route path="advanced-reports" element={<AdvancedReportsPage />} />
            <Route path="support" element={<SupportPage />} />
          </Route>

          {/* Barbero routes */}
          <Route path="/barber" element={
            <ProtectedRoute roles={['barbero']}>
              <AppLayout />
            </ProtectedRoute>
          }>
            <Route index element={<Navigate to="calendar" replace />} />
            <Route path="calendar" element={<BarberCalendarPage />} />
            <Route path="appointments" element={<BarberAppointmentsPage />} />
            <Route path="support" element={<SupportPage />} />
          </Route>

          {/* Super Admin routes */}
          <Route path="/superadmin" element={
            <ProtectedRoute roles={['super_admin']}>
              <AppLayout />
            </ProtectedRoute>
          }>
            <Route index element={<SuperDashboard />} />
            <Route path="businesses" element={<BusinessesPage />} />
            <Route path="plans" element={<PlansPage />} />
            <Route path="users" element={<UsersPage />} />
            <Route path="reports" element={<SuperReportsPage />} />
            <Route path="tickets" element={<TicketsPage />} />
          </Route>

          {/* 404 */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
